import { gql, useLazyQuery } from '@apollo/client';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import { playbackDateAtom } from 'atoms';
import { useMessages } from 'hooks';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { Account } from 'types/Account';
import ExportImageIcon from './ExportImageIcon';
import downloadURI from 'services/downloadURI';
import dayjs from 'dayjs';

const CAMERA_SNAP_URL = gql`
  query GetCameraSnapURL(
    $isCloud: Boolean!
    $masterCompanyId: Int!
    $accountId: Int!
    $accountType: Int!
    $doorId: Int
    $email: String
    $isSIMCloud: Boolean
    $userName: String
    $camName: String!
    $dateTime: String
    $timezone: Int
  ) {
    getCameraSnapURL(
      isCloud: $isCloud
      masterCompanyId: $masterCompanyId
      accountId: $accountId
      accountType: $accountType
      doorId: $doorId
      email: $email
      isSIMCloud: $isSIMCloud
      userName: $userName
      camName: $camName
      dateTime: $dateTime
      timezone: $timezone
    ) {
      url
      token
    }
  }
`;

function ExportImage({ accountSelected, camName }: { accountSelected: Account; camName: string }) {
  const { t } = useTranslation('cameras');
  const playbackDate = useRecoilValue(playbackDateAtom);
  const { enqueueError } = useMessages();

  const [getUrl, { loading, error }] = useLazyQuery<{
    getCameraSnapURL: { url: string; token: string };
  }>(CAMERA_SNAP_URL, {
    fetchPolicy: 'no-cache',
    variables: {
      ...accountSelected,
      accountId: accountSelected.id,
      camName,
      dateTime: playbackDate,
    },
  });

  const handleGetImage = React.useCallback(async () => {
    const retorno = await getUrl();
    if (retorno.data?.getCameraSnapURL) {
      downloadURI(
        retorno.data?.getCameraSnapURL.url,
        `${camName}-${dayjs(playbackDate || undefined).format('L LTS')}`,
        retorno.data?.getCameraSnapURL.token,
      );
    }
    if (error) {
      enqueueError(t('error_exporting_image'));
    }
  }, [camName, enqueueError, error, getUrl, playbackDate, t]);

  if (loading) {
    return (
      <IconButton size="small">
        <CircularProgress size={24} />
      </IconButton>
    );
  }

  return (
    <Tooltip title={t('export_image')} arrow>
      <IconButton color="inherit" size="small" onClick={() => handleGetImage()}>
        <ExportImageIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

export default ExportImage;
